import { Dialog, Heading, Text } from '@soteria/design-system-ve';

interface ClassificationPromptsMoreInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ClassificationPromptsMoreInfoDialog Component
 *
 * Provides detailed information about classification prompts, including
 * what they are, when they're regenerated automatically, and when manual
 * generation is needed.
 */
export function ClassificationPromptsMoreInfoDialog({ isOpen, onClose }: ClassificationPromptsMoreInfoDialogProps) {
  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title="About Safety Prompts" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <Heading level="h3" style={{ marginBottom: '8px' }}>
            What are Safety Prompts?
          </Heading>
          <Text>
            Safety prompts are AI-generated instructions that guide the classification service in detecting harmful content. Each prompt consists of harm
            definitions for applicable labels (hate speech, harassment, violence, etc.) tailored to your community's specific context using Azure OpenAI.
          </Text>
        </div>

        <div>
          <Heading level="h3" style={{ marginBottom: '8px' }}>
            Production vs Inactive Versions
          </Heading>
          <Text>
            Each community maintains multiple prompt versions. The "Production" version is actively used by the classification service to evaluate all content
            in your community. "Inactive" versions are available for testing and comparison. Promoting a version to production means designating it as the
            active version that will be used for all classification operations.
          </Text>
        </div>

        <div>
          <Heading level="h3" style={{ marginBottom: '8px' }}>
            Automatic Regeneration
          </Heading>
          <Text>
            Prompts are automatically regenerated when standard label definitions change (policy files are updated), when community metadata changes
            (description, PG rating, genres, or guidelines URL are modified), or when false positives or false negatives are reported. If nothing has changed
            and no feedback is available, generation is skipped.
          </Text>
        </div>

        <div>
          <Heading level="h3" style={{ marginBottom: '8px' }}>
            Manual Generation
          </Heading>
          <Text>
            You should manually generate prompts for new community setup, to force regeneration after updating system prompt templates, to create new versions
            for A/B testing before promoting to production, or after making significant changes to community guidelines when you want to ensure prompts reflect
            the latest context.
          </Text>
        </div>
      </div>
    </Dialog>
  );
}
